import React from 'react'
import './user-role-editor.scss'
import { classnames } from '@halo/_utils/classnames'
import { Well, Drawer } from '@pushly/aqe/lib/components'
import { Form, Input, Switch, Radio } from 'antd'
import { IUserRole } from '@halo/interfaces/user-role'
import { DEFAULT_DRAWER_FORM_ITEM_LAYOUT } from '@halo/constants'
import * as deepEqual from 'react-fast-compare'
import { UserTypeId } from '@halo/enums/user-type'
import { isInternalRole } from '@halo/_utils/user-role'

interface IUserRoleEditorProps {
    userRole: IUserRole
    onClose: () => unknown
    onSubmit: (values: Partial<IUserRole>, userRole: IUserRole) => unknown
    visible: boolean
}

const UserRoleEditor: React.FunctionComponent<IUserRoleEditorProps> = ({ userRole, ...props }) => {
    const [form] = Form.useForm()

    const internal = isInternalRole(userRole)

    const currValues = {
        name: userRole.name,
        description: userRole.description,
        user_type_id: userRole.user_type_id,
        is_active: userRole.is_active,
        // Only for internal roles: a database constraint rejects the grant on any other user
        // type, so offering the field would submit a request that can only fail.
        ...(internal ? { has_network_scope: userRole.has_network_scope } : {}),
    }

    const [initialFormValues, setInitialFormValues] = React.useState(currValues)
    const [submitDisabled, setSubmitDisabled] = React.useState(true)

    /**
     * if the passed userRole values change the form should reset
     * and update initial values for submit btn state observation
     */
    React.useEffect(() => {
        if (!deepEqual(initialFormValues, currValues)) {
            setSubmitDisabled(true)
            setInitialFormValues(currValues)
            form.setFieldsValue(currValues)
        }
    }, [...Object.values(currValues)])

    return (
        <Drawer
            className={classnames('user-role-editor-drawer')}
            width={800}
            title={userRole.id ? `Edit: ${userRole.description ?? userRole.name}` : 'New User Role'}
            onClose={props.onClose}
            onSubmit={async () => {
                try {
                    await form.validateFields()
                    const formValues = form.getFieldsValue()

                    const changedValues: any = {}
                    for (const key of Object.keys(formValues) as Array<keyof IUserRole>) {
                        if (formValues[key] !== userRole[key]) {
                            changedValues[key] = formValues[key]
                        }
                    }

                    props.onSubmit(changedValues, userRole)
                } catch {}
            }}
            disableSubmit={submitDisabled}
            open={props.visible ?? false}
            getContainer={false}
            destroyOnClose={true}
        >
            <Well title="User Role Details" variant="ghost" footerMode="hidden">
                <Form
                    {...DEFAULT_DRAWER_FORM_ITEM_LAYOUT}
                    form={form}
                    initialValues={initialFormValues}
                    size="small"
                    labelAlign="left"
                    colon={false}
                    requiredMark={false}
                    onValuesChange={(_fieldKV, formKV) => setSubmitDisabled(deepEqual(formKV, initialFormValues))}
                >
                    <Form.Item name="name" label="Name" required={true}>
                        <Input disabled={!!userRole.id} />
                    </Form.Item>
                    <Form.Item name="user_type_id" label="User Type">
                        <Radio.Group size="small" buttonStyle="solid" disabled={!!userRole.id}>
                            <Radio.Button value={UserTypeId.INTERNAL}>Internal</Radio.Button>
                            <Radio.Button value={UserTypeId.EXTERNAL}>External</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item name="description" label="Description" required={true}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="is_active" label="Enabled" valuePropName="checked">
                        <Switch size="small" />
                    </Form.Item>
                    {internal && (
                        <Form.Item
                            name="has_network_scope"
                            label="Network Scope"
                            valuePropName="checked"
                            extra="Every user holding this role reaches every organization and domain, whether or not they hold a row for it."
                        >
                            <Switch size="small" />
                        </Form.Item>
                    )}
                </Form>
            </Well>
        </Drawer>
    )
}

export default UserRoleEditor
